/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { saveAs } from 'file-saver';
import contentDisposition from 'content-disposition';
import { AxiosInstance, AxiosResponse } from 'axios';

import { DocumentsAPI } from '../common/documents';
import { baseUrl } from './types';

export const documentsClient = (request: AxiosInstance) => ({
  async fetchAction(callId: string): Promise<DocumentsAPI.ActionResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/action`)).data;
  },

  async fetchCallDocuments(callId: string): Promise<DocumentsAPI.DocumentsResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/documents`)).data;
  },

  async fetchRoomDocuments(roomId: string): Promise<DocumentsAPI.DocumentsResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/documents`)).data;
  },

  async fetchCallDocumentPdf(callId: string, documentId: string): Promise<string> {
    const res = await request.get(`${baseUrl}/calls/${callId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const file = new Blob([res.data], {
      type: 'application/pdf',
    });
    return URL.createObjectURL(file);
  },

  async fetchRoomDocumentPdf(roomId: string, documentId: string): Promise<string> {
    const res = await request.get(`${baseUrl}/rooms/${roomId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const file = new Blob([res.data], {
      type: 'application/pdf',
    });
    return URL.createObjectURL(file);
  },

  async saveRoomDocumentPdf(roomId: string, documentId: string): Promise<void> {
    const res = await request.get(`${baseUrl}/rooms/${roomId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const cd = contentDisposition.parse(res.headers['content-disposition'] as string);
    const file = new Blob([res.data], {
      type: 'application/pdf',
    });
    saveAs(file, cd.parameters.filename);
  },

  async fetchCallDocumentEditUrl(callId: string, documentId: string): Promise<DocumentsAPI.UrlResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/documents/${documentId}/edit`)).data;
  },

  async fetchRoomDocumentEditUrl(roomId: string): Promise<DocumentsAPI.UrlResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/documents/edit-url`)).data;
  },

  async fetchDocumentSigningUrl(callId: string, documentId: string): Promise<DocumentsAPI.UrlResponse> {
    return (await request.get(`${baseUrl}/calls/${callId}/documents/${documentId}/sign`)).data;
  },

  async voidDocument(callId: string, documentId: string): Promise<void> {
    await request.put(`${baseUrl}/calls/${callId}/documents/${documentId}/void`);
  },

  async reviewDocument(callId: string, documentId: string): Promise<void> {
    await request.post(`${baseUrl}/calls/${callId}/documents/${documentId}/review`);
  },

  async uploadDocumentToRoom(roomId: string, file: File): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);

    const requestConfig = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
    return request.post(`${baseUrl}/rooms/${roomId}/documents`, formData, requestConfig);
  },

  async discardDocument(roomId: string, documentId: string): Promise<void> {
    await request.post(`${baseUrl}/rooms/${roomId}/documents/${documentId}/discard`);
  },

  async restoreDocument(roomId: string, documentId: string): Promise<void> {
    await request.post(`${baseUrl}/rooms/${roomId}/documents/${documentId}/restore`);
  },

  async orderDocuments(roomId: string, docOrder: string[]): Promise<void> {
    await request.patch(`${baseUrl}/rooms/${roomId}/documents/update-doc-order`, { docOrder });
  },

  async finalizeCallDocument(callId: string, documentId: string): Promise<void> {
    await request.post(`${baseUrl}/calls/${callId}/documents/${documentId}/finalize`);
  },

  async finalizeRoomDocument(roomId: string, documentId: string): Promise<void> {
    await request.post(`${baseUrl}/rooms/${roomId}/documents/${documentId}/finalize`);
  },

  async handleSigning(callId: string, documentId: string): Promise<void> {
    await request.get(`${baseUrl}/calls/${callId}/documents/${documentId}/handle-signing`);
  },

  async resetDocument(callId: string, documentId: string): Promise<void> {
    await request.put(`${baseUrl}/calls/${callId}/documents/${documentId}/reset`);
  },

  async deleteRoomDocument(roomId: string, documentId: string, callId: string): Promise<void> {
    await request.post(`${baseUrl}/rooms/${roomId}/documents/${documentId}`, { callId });
  },
});
